import * as React from "react";
import {
  SafeAreaView,
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  StatusBar,
} from "react-native";
import { Icon, Text, theme, typography } from "ui-components";
import { DomusoIcon } from "../../../../assets/domuso-icon";
import { Grid, Container, Row, Column } from "ui-grid";
import { BottomNav } from "../../layout/bottom-nav";
import { Footer } from "../footer/footer";
import { domusoResponsiveStyleHelper } from "../../../screens/new/utils/domuso-responsive";
import { translate } from "../../../i18n/translate";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { StackActions, useNavigation } from "@react-navigation/native";
import { RewardsType } from "../../../enums";
import { getRewardByRewardType } from "../../../screens/new/utils/helper";
import { RewardsLedgerItem } from "../../../../types/db-types";
import { RewardsData } from "../../../redux/reducers/reward";
import { updateRewardAction } from "../../../redux/actions/actionCreators/reward";
import { FullScreenLoader } from "../loader/full-screen-loader";

export interface RewardsIntroSplashScreenProps {
  stepTitle?: string;
  stepDescription?: string;
  children?: any;
  totalNumberOfSteps?: number;
  currentStep?: number;
  onPressPrevious?: any;
  onPressNext?: any;
  buttonsActiveOpacity?: number;
  testID?: string;
  accessibilityLabel?: string;
}
export const RewardsIntroSplashScreen = (props: RewardsIntroSplashScreenProps) => {
  const {
    stepTitle,
    stepDescription,
    children,
    currentStep,
    totalNumberOfSteps = 3,
    onPressNext,
    onPressPrevious,
    buttonsActiveOpacity = 0.6,
    accessibilityLabel,
    testID,
  } = props;

  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const dispatch = useDispatch();
  const [firstLoginReward, setFirstLoginReward] = React.useState<RewardsLedgerItem>();

  let previousButtonDisabled = currentStep == 1;
  let previousButtonColor = previousButtonDisabled ? theme.colors.text2 : theme.colors.primary;
  let nextButtonColor =
    currentStep === totalNumberOfSteps ? theme.colors.white : theme.colors.primary;
  let dotBackgroundColor = (index: number) =>
    index + 1 === currentStep ? theme.colors.primary : theme.colors.stroke;

  const rewardsData = useSelector((state: RootState) => state.reward.data) as RewardsData;
  const loading = useSelector((state: RootState) => state.reward.loading);

  const { reservations } = useSelector((state: RootState) => state.reservation);
  const navigation = useNavigation();

  const navigateToHome = () => {
    navigation.dispatch(StackActions.replace("Reservation"));
  };
  const navigateToRewardsPage = () => {
    navigation.navigate("Actions", {
      screen: "Rewards",
    });
  };

  React.useEffect(() => {
    setFirstLoginReward(getRewardByRewardType(rewardsData, RewardsType?.FIRST_LOGIN));
  }, [rewardsData]);

  const updateFirstLoginReward = async (button: "skip" | "rewards") => {
    try {
      const body = {
        cognitoUser,
        propertyId: reservations[0]?.property?.id,
        accountingId: reservations[0]?.accounting_id,
        ledgerItemId: firstLoginReward?.id,
        body: { redeemed_at: new Date() },
      };
      await dispatch(updateRewardAction(body));
      if (button == "rewards") {
        navigateToHome();
        navigateToRewardsPage();
      } else {
        navigateToHome();
      }
    } catch (error) {
      console.log(error);
      navigateToHome();
    }
  };
  return (
    <SafeAreaView style={styles.container}>
      <FullScreenLoader loading={loading} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <StatusBar
          hidden={false}
          translucent={false}
          backgroundColor={theme.colors.white}
          barStyle={"dark-content"}
        />
        <View style={{ flex: 1 }}>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column
                  span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                  style={[styles.inputRow, styles.header]}></Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={[styles.inputRowInner, styles.bodyContainer]}>
                    <TouchableOpacity
                      style={{ alignSelf: "flex-end", outlineStyle: "none" } as any}
                      onPress={() => updateFirstLoginReward("skip")}
                      activeOpacity={buttonsActiveOpacity}
                      testID={testID + "-skip-button"}
                      accessibilityLabel={accessibilityLabel + " skip button"}>
                      <Text
                        style={styles.skipText}
                        testID={testID + "-skip-button-text"}
                        accessibilityLabel={accessibilityLabel + " skip button text"}>
                        {translate("rewardsIntroSplashScreen.skip")}
                      </Text>
                    </TouchableOpacity>
                    <View style={{ marginTop: 15 }}>
                      <DomusoIcon
                        width={40}
                        height={55}
                        testID={testID + "-domuso-icon-image"}
                        accessibilityLabel={accessibilityLabel + " domuso icon image"}
                      />
                    </View>
                    <View style={{ marginTop: 30 }}>
                      <Text
                        style={styles.introductionHeader}
                        testID={testID + "-header"}
                        accessibilityLabel={accessibilityLabel + " header"}>
                        {translate("rewardsIntroSplashScreen.title")}
                      </Text>
                    </View>
                    <View style={styles.rewardIntroImageContainer}>{children}</View>
                    <View style={{ marginTop: 35 }}>
                      <Text
                        style={styles.stepTitle}
                        testID={testID + "-step-title"}
                        accessibilityLabel={accessibilityLabel + " step title"}>
                        {stepTitle}
                      </Text>
                    </View>
                    <View style={{ marginTop: 15 }}>
                      <Text
                        style={styles.stepDescription}
                        testID={testID + "-step-description"}
                        accessibilityLabel={accessibilityLabel + " step description"}>
                        {stepDescription}
                      </Text>
                    </View>
                    {currentStep === totalNumberOfSteps && (
                      <View
                        style={styles.congratsBox}
                        testID={testID + "-congrats-box"}
                        accessibilityLabel={accessibilityLabel + " congrats box"}>
                        <Text
                          style={styles.congratsTitle}
                          testID={testID + "-congrats-title"}
                          accessibilityLabel={accessibilityLabel + " congrats title"}>
                          {translate("rewardsIntroSplashScreen.congratulations")}!
                        </Text>
                        <Text
                          style={styles.congratsDesc}
                          testID={testID + "-congrats-description"}
                          accessibilityLabel={accessibilityLabel + " congrats description"}>
                          {`${translate("rewardsIntroSplashScreen.congratsDescription1")} ${
                            firstLoginReward?.points
                          } ${translate("rewardsIntroSplashScreen.congratsDescription2")}!`}
                        </Text>
                      </View>
                    )}
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
        </View>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <View style={[styles.inputRowInner, styles.bottomNavContainerStyle]}>
          <View style={styles.prevNextContainerStyle}>
            <TouchableOpacity
              style={styles.prevNextButtonStyle}
              onPress={onPressPrevious}
              activeOpacity={buttonsActiveOpacity}
              disabled={previousButtonDisabled}
              testID={testID + "-previous-button"}
              accessibilityLabel={accessibilityLabel + " previous button"}>
              <Icon
                name="direction-left"
                size={24}
                color={previousButtonColor}
                testID={testID + "-previous-button-icon"}
              />
              <Text
                testID={testID + "-previous-button-text"}
                accessibilityLabel={accessibilityLabel + " previous button text"}
                style={[styles.prevNextTextStyle, { color: previousButtonColor }]}>
                {translate("rewardsIntroSplashScreen.previous")}
              </Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.prevNextContainerStyle, { justifyContent: "center" }]}>
            <View style={styles.prevNextButtonStyle}>
              {Array.from({ length: totalNumberOfSteps }, (item, index) => (
                <View
                  key={index}
                  style={[styles.dotStyle, { backgroundColor: dotBackgroundColor(index) }]}
                  testID={testID + `-dot-${index}`}
                  accessibilityLabel={accessibilityLabel + ` dot ${index}`}
                />
              ))}
            </View>
          </View>
          <View style={[styles.prevNextContainerStyle, { justifyContent: "flex-end" }]}>
            <TouchableOpacity
              testID={testID + "-next-button"}
              accessibilityLabel={accessibilityLabel + " next button"}
              style={[
                styles.prevNextButtonStyle,
                currentStep === totalNumberOfSteps ? styles.lastStepNextButtonStyle : {},
              ]}
              onPress={() =>
                currentStep === totalNumberOfSteps
                  ? updateFirstLoginReward("rewards")
                  : onPressNext()
              }
              activeOpacity={buttonsActiveOpacity}>
              <Text
                testID={testID + "-next-button-text"}
                accessibilityLabel={accessibilityLabel + " next button text"}
                style={[styles.prevNextTextStyle, { color: nextButtonColor }]}>
                {currentStep === totalNumberOfSteps
                  ? translate("rewardsIntroSplashScreen.rewards")
                  : translate("rewardsIntroSplashScreen.next")}
              </Text>
              <Icon
                testID={testID + "-next-button-icon"}
                name="direction-right"
                size={24}
                color={nextButtonColor}
              />
            </TouchableOpacity>
          </View>
        </View>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: theme.colors.white,
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: theme.colors.white,
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: theme.colors.white,
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: {
    ...domusoResponsiveStyleHelper({
      webPhone: { flexGrow: 0 },
      webTablet: { flexGrow: 0 },
      webDesktop: { flexGrow: 0 },
      iosPhone: { flex: 1 },
      iosTablet: { flexGrow: 0 },
      androidPhone: { flex: 1 },
      androidTablet: { flexGrow: 0 },
    }),
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    }),
  },
  header: {
    ...domusoResponsiveStyleHelper({
      webPhone: { paddingVertical: 3 },
      webTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      webDesktop: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      iosPhone: { paddingVertical: 3 },
      iosTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      androidPhone: { paddingVertical: 3 },
      androidTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
    }),
  },
  skipText: { ...typography.title2, color: theme.colors.primary },
  introductionHeader: {
    ...typography.title1,
    color: theme.colors.secondary,
    fontFamily: "Lato_700Bold",
    textAlign: "center",
  },
  stepTitle: {
    ...typography.heading2,
    color: theme.colors.text4,
    textAlign: "center",
    fontWeight: "500",
  },
  stepDescription: {
    ...typography.title2,
    color: theme.colors.text2,
    textAlign: "center",
    fontWeight: "500",
  },
  prevNextButtonStyle: {
    flexDirection: "row",
    alignItems: "center",
    padding: 1,
    outlineStyle: "none",
    justifyContent: "center",
  },
  lastStepNextButtonStyle: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    borderRadius: 50,
    paddingVertical: 4,
  },
  prevNextTextStyle: {
    ...typography.title2,
    color: theme.colors.primary,
    textAlign: "center",
    fontWeight: "500",
  },
  dotStyle: {
    height: 8,
    width: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.primary,
    margin: 6,
  },
  prevNextContainerStyle: {
    flex: 1,
    flexDirection: "row",
  },
  bottomNavContainerStyle: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    ...domusoResponsiveStyleHelper({
      webPhone: { marginTop: 20 },
      webTablet: { marginTop: 30 },
      webDesktop: { marginTop: 40 },
      iosPhone: {},
      iosTablet: { marginTop: 30 },
      androidPhone: {},
      androidTablet: { marginTop: 30 },
    }),
  },
  skipButtonStyle: {},
  rewardIntroImageContainer: {
    marginTop: 40,
    height: 289,
    width: "100%",
    justifyContent: "flex-end",
  },
  bodyContainer: {
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  congratsBox: {
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: theme.colors.success,
    width: "100%",
    marginVertical: 15,
    marginTop: 20,
  },
  congratsTitle: {
    ...typography.title2,
    textAlign: "center",
    fontWeight: "600",
    color: theme.colors.success,
  },
  congratsDesc: {
    ...typography.title2,
    textAlign: "center",
    color: theme.colors.text4,
  },
});
